/**
 * Tap feedback while the next page loads (owner, 2026-09-21: "when i search on product i click
 * sometimes it feel stuck and takes a long time").
 *
 * A tap on a link keeps the old page on screen, untouched, until the next page paints: on a phone
 * network that is one to three seconds of a screen that looks frozen. The moment a link to another
 * page of the shop is followed, this dims the tapped link ([data-navigating]) and runs a thin brand
 * bar across the top (.nav-progress). The bar goes inside the open modal dialog when there is one
 * (the search dialog sits in the top layer, above anything on the page itself).
 *
 * It never changes the navigation: it listens on window, after every other click handler, and does
 * nothing when a handler already took the click (defaultPrevented), for a new tab or window, a
 * download, another site, or a link to the same page. A page restored from the back/forward cache
 * is cleaned up, and the bar gives up after 15 s in case the navigation never happens.
 */

/**
 * matches() that answers false instead of throwing on a selector the browser does not know
 * (:focus-visible before Safari 15.4, :modal before Safari 15.6 / Chrome 105).
 * @param {Element | null} element
 * @param {string} selector
 */
function safeMatches(element, selector) {
  try {
    return Boolean(element?.matches(selector));
  } catch {
    return false;
  }
}
const GIVE_UP_MS = 15000;
let timer = 0;

function clear() {
  window.clearTimeout(timer);
  timer = 0;
  for (const bar of document.querySelectorAll('.nav-progress')) bar.remove();
  for (const link of document.querySelectorAll('[data-navigating]')) link.removeAttribute('data-navigating');
}

/** @param {HTMLAnchorElement} link */
function start(link) {
  clear();
  link.setAttribute('data-navigating', '');
  const dialogs = [...document.querySelectorAll('dialog[open]')].filter((dialog) => safeMatches(dialog, ':modal'));
  const host = dialogs.at(-1) ?? document.body;
  const bar = document.createElement('div');
  bar.className = 'nav-progress';
  bar.setAttribute('aria-hidden', 'true');
  host.append(bar);
  timer = window.setTimeout(clear, GIVE_UP_MS);
}

window.addEventListener('click', (event) => {
  if (event.defaultPrevented || event.button !== 0) return;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const target = event.target;
  const link = target instanceof Element ? target.closest('a[href]') : null;
  if (!(link instanceof HTMLAnchorElement)) return;
  if ((link.target && link.target !== '_self') || link.hasAttribute('download')) return;

  const url = new URL(link.href, window.location.href);
  if (url.origin !== window.location.origin || !/^https?:$/.test(url.protocol)) return;
  if (url.pathname === window.location.pathname && url.search === window.location.search) return;
  start(link);
});

window.addEventListener('pageshow', (event) => {
  if (event.persisted) clear();
});
